import ReportChartPage, {
  ReportChartConfig,
} from '@app/components/reports/components/report-chart-page';
import {
  carbonIntensityAxisProps,
  carbonIntensityChartConfig,
  emissionsChartConfig,
  emissionsWithCarbonIntensityConfig,
  emissionsWithGoodsTypeConfig,
  emissionsWithSpendConfig,
  spendAxisProps,
  spendChartConfig,
  totalEmissionsAxisProps,
} from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useEmissionsChartVariants,
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import { useAccount } from '@shared/api';

import DualAxisBarChart from '@shared/components/chart/dual-axis-bar-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import {
  getXAxisInterval,
  truncateLabel,
} from '@shared/components/chart/utils';
import Text from '@shared/components/content/text';
import { getCurrencySymbol } from '@shared/lib/format';

interface EmissionsBySiteProps extends ReportSectionComponent {}

export default function EmissionsBySite({ reportType }: EmissionsBySiteProps) {
  const {
    result: { emissionsBySite },
    downloadMode,
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);
  const { variant, setVariant, availableVariants, variants } =
    useEmissionsChartVariants(reportType, 'site');

  const { data: account } = useAccount(true);
  const currencySymbol = getCurrencySymbol(account?.defaultCurrency);

  const emissionsWithCarbonIntensityData = emissionsBySite;
  const emissionsWithGoodsType = emissionsBySite.map((item) => {
    const result: Record<string, number | string> = { name: item.name };
    item.byGoodsType.forEach((type) => {
      result[type.name.toLocaleLowerCase()] = type.emissionsTonnes;
    });
    return result;
  });

  const emissionsWithSpendData = emissionsBySite.map((item) => ({
    ...item,
    totalSpend: item.totalSpend != null ? Number(item.totalSpend) : 0,
  }));

  const emissionsDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map((d) => d.emissionsTonnes)
  );
  const carbonIntensityDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map((d) => d.carbonIntensity)
  );
  const spendDataMax = Math.max(
    0,
    ...emissionsWithSpendData.map((d) => d.totalSpend)
  );

  const defaultChartProps = {
    nameKey: 'name',
    dataKey: 'name',
    margin: { top: 40, right: 10, left: 0, bottom: 30 },
    className: 'h-max max-h-[calc(var(--content-title-height)-20rem)] w-full',
    XAxisProps: {
      interval: 0,
      angle: -30,
      textAnchor: 'end',
      tickMargin: 2,
      dx: 10,
      tickFormatter: (value) => truncateLabel(value, 15),
    },
  };

  let config: ReportChartConfig = emissionsChartConfig;
  if (variant === variants.goodsType) {
    config = emissionsWithGoodsTypeConfig;
  } else if (variant === variants.intensity) {
    config = emissionsWithCarbonIntensityConfig;
  } else if (variant === variants.spend) {
    config = emissionsWithSpendConfig(currencySymbol);
  }

  return (
    <ReportChartPage
      title="Total emissions per site"
      description={
        <Text variant="reportBody">
          This chart shows the total emissions at each of your sites, ordered by
          highest emissions. Emissions from {reportTypeCopy.plural} not
          associated with a site are grouped under <em>No site</em>.
        </Text>
      }
      variantOptions={availableVariants}
      variant={variant}
      setVariant={setVariant}
      variantsEnabled={!downloadMode && availableVariants.length > 1}
      chartConfig={config}
    >
      {variant === variants.emissions && (
        <SimpleBarChart
          {...defaultChartProps}
          chartConfig={emissionsChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithCarbonIntensityData.length),
          }}
        />
      )}
      {variant === variants.intensity && (
        <DualAxisBarChart
          {...defaultChartProps}
          leftAxisConfig={emissionsChartConfig}
          rightAxisConfig={carbonIntensityChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={carbonIntensityAxisProps(carbonIntensityDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithCarbonIntensityData.length),
          }}
        />
      )}
      {variant === variants.goodsType && (
        <SimpleBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsWithGoodsType}
          chartConfig={emissionsWithGoodsTypeConfig}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithGoodsType.length),
          }}
        />
      )}
      {variant === variants.spend && (
        <DualAxisBarChart
          {...defaultChartProps}
          leftAxisConfig={emissionsChartConfig}
          rightAxisConfig={spendChartConfig(currencySymbol)}
          chartData={emissionsWithSpendData}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={spendAxisProps(spendDataMax, currencySymbol)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithSpendData.length),
          }}
        />
      )}
    </ReportChartPage>
  );
}
